import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const RunningPace = () => {
  // State variables for Running Pace calculation
  const [totalTime, setTotalTime] = useState(0); // Total time in minutes
  const [distance, setDistance] = useState(0); // Distance in miles or kilometers
  const [pace, setPace] = useState(0); // Calculated pace

  // Event handlers to update state values
  const handleTotalTimeChange = (e) => setTotalTime(parseFloat(e.target.value) || 0);
  const handleDistanceChange = (e) => setDistance(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate pace when any input changes
  useEffect(() => {
    if (totalTime && distance) {
      const runningPace = totalTime / distance; // Calculate pace
      setPace(runningPace);
    } else {
      setPace(0);
    }
  }, [totalTime, distance]);

  return (
    <Layout title="Running Pace Calculator" path="running-pace">
      <section className="section-padding" id="running-pace-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Running Pace Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Running Pace Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Total Time Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalTime" className="form-label">
                        Total Time (minutes)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalTime"
                        placeholder="Enter total time in minutes"
                        value={totalTime || ""}
                        onChange={handleTotalTimeChange}
                      />
                    </div>

                    {/* Distance Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="distance" className="form-label">
                        Distance (miles or kilometers)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="distance"
                        placeholder="Enter distance covered"
                        value={distance || ""}
                        onChange={handleDistanceChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Running Pace:{" "}
                        <span className="fw-bold ms-3">
                          {pace === 0 ? "0.00" : pace.toFixed(2)} minutes per mile
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Running Pace Calculator
                </h3>

                {/* What is a Running Pace Calculator? */}
                <h4 className="mt-3 mb-3">What is a Running Pace Calculator?</h4>
                <p>
                  A **Running Pace Calculator** helps you determine how long it takes to cover one unit of distance (mile or kilometer) based on your total running time. This is essential for assessing running performance, improving fitness, and setting running goals.
                </p>

                {/* Why Use a Running Pace Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Running Pace Calculator?</h4>
                <p>
                  By calculating your running pace, you can measure improvements over time, ensure you’re running at the correct speed for your fitness goals, and adjust your training program accordingly.
                </p>

                {/* Benefits of Using a Running Pace Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Running Pace Calculator</h4>
                <ul>
                  <li><strong>Useful for Fitness Tracking and Goal Setting:</strong> Helps runners track their performance and progress.</li>
                  <li><strong>Assists Runners in Optimizing Their Performance:</strong> Provides a better understanding of pacing, which helps in optimizing endurance.</li>
                  <li><strong>Allows Athletes to Monitor Training Progress:</strong> Enables athletes to assess improvements over time and adjust their workout routines.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Running Pace Calculator?</h4>
                <p>
                  The Running Pace Calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Health and Fitness Apps:</strong> For users to track and improve their running pace over time.</li>
                  <li><strong>Personal Trainers:</strong> Helps trainers monitor the progress of their clients and optimize training plans.</li>
                  <li><strong>Athletes:</strong> Assists competitive runners in preparing for races and tracking performance during training sessions.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How can I use pace to improve my running performance?</h4>
                <p>
                  By knowing your running pace, you can tailor your training to either increase your speed or endurance, depending on your fitness goals. Monitoring pace allows you to gradually push yourself for better results.
                </p>

                <h4 className="mt-4 mb-3">Should I focus on pace or distance?</h4>
                <p>
                  Both pace and distance are important, but focusing on pace helps improve the efficiency of your runs. If you're training for speed, focus on improving your pace, while gradually increasing your distance as you build stamina.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RunningPace;
